import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { GFM_SITES } from "../lib/permitSchemas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Loader2, UserCheck, X, Trash2, Plus, Clock, MapPin } from "lucide-react";

function ApproveDialog({ open, onOpenChange, user, onDone }) {
  const [role, setRole] = useState("site_user");
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setRole("site_user");
      setSites(user.suggested_site ? [user.suggested_site] : []);
    }
  }, [user]);

  const toggle = (s) => setSites((v) => (v.includes(s) ? v.filter((x) => x !== s) : [...v, s]));

  const submit = async () => {
    if (role === "site_user" && sites.length === 0) return toast.error("Assign at least one site.");
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/approve`, { role, sites });
      toast.success(`${user.email} approved`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle>Approve {user?.name}</DialogTitle>
          <DialogDescription>Confirm this user's role and the site(s) they can manage.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md bg-secondary/60 p-3 text-sm">
            <div>{user?.email}</div>
            {user?.department && <div className="text-xs text-muted-foreground">Microsoft department: <strong>{user.department}</strong></div>}
            {user?.suggested_site && <div className="text-xs text-brand">Auto-matched site: {user.suggested_site}</div>}
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v)}>
              <SelectTrigger data-testid="approve-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="site_user">Site user</SelectItem>
                <SelectItem value="admin">Admin (full access)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role === "site_user" && (
            <div className="space-y-2">
              <Label>Assigned sites</Label>
              <div className="grid gap-2 rounded-md border border-border p-3">
                {GFM_SITES.map((s) => (
                  <label key={s} className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox checked={sites.includes(s)} onCheckedChange={() => toggle(s)} data-testid={`approve-site-${s}`} />
                    {s}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-dark" onClick={submit} disabled={saving} data-testid="confirm-approve-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminApprovals() {
  const [pending, setPending] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [approve, setApprove] = useState(null);
  const [newDept, setNewDept] = useState("");
  const [newSite, setNewSite] = useState(GFM_SITES[0]);

  const load = () => {
    api.get("/admin/pending-users").then((r) => setPending(r.data)).catch(() => setPending([]));
    api.get("/admin/site-mappings").then((r) => setMappings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const reject = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/reject`);
      toast.success(`${u.email} rejected`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const addMapping = async () => {
    if (!newDept.trim()) return toast.error("Enter a department.");
    try {
      await api.post("/admin/site-mappings", { department: newDept.trim(), site: newSite });
      toast.success("Mapping saved");
      setNewDept("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const delMapping = async (id) => {
    try { await api.delete(`/admin/site-mappings/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">GFM - Permit to Work Management System</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Microsoft approvals</h1>
        <p className="text-sm text-muted-foreground">Authorise new Microsoft sign-ins and manage how departments map to sites.</p>
      </div>

      <div className="mb-10 rounded-md border border-border bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-border p-4">
          <Clock className="h-4 w-4 text-amber-600" />
          <h2 className="font-heading text-lg font-semibold">Pending approvals</h2>
          {pending && pending.length > 0 && <Badge className="bg-amber-500 text-white">{pending.length}</Badge>}
        </div>
        {pending === null ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
        ) : pending.length === 0 ? (
          <div className="px-4 py-10 text-center text-muted-foreground" data-testid="no-pending">No one is waiting for approval.</div>
        ) : (
          <table className="w-full text-sm" data-testid="pending-table">
            <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Department</th><th className="px-4 py-3">Auto-matched site</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {pending.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`pending-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">{u.department || "—"}</td>
                  <td className="px-4 py-3">{u.suggested_site ? <span className="inline-flex items-center gap-1 text-brand"><MapPin className="h-3 w-3" />{u.suggested_site}</span> : <span className="text-muted-foreground">No match</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" className="bg-brand hover:bg-brand-dark" onClick={() => setApprove(u)} data-testid={`approve-btn-${u.email}`}><UserCheck className="mr-1 h-4 w-4" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => reject(u)} data-testid={`reject-btn-${u.email}`}><X className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-md border border-border bg-white shadow-sm">
        <div className="border-b border-border p-4">
          <h2 className="font-heading text-lg font-semibold">Department → Site mapping</h2>
          <p className="text-xs text-muted-foreground">When a Microsoft user signs in, their department is matched here (or by exact site name) to pre-fill their site.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3 border-b border-border p-4">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <Label>Department (from Microsoft)</Label>
            <Input value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="e.g. Grounds & Estates" data-testid="mapping-dept" />
          </div>
          <div className="w-64 space-y-1.5">
            <Label>Site</Label>
            <Select value={newSite} onValueChange={setNewSite}>
              <SelectTrigger data-testid="mapping-site"><SelectValue /></SelectTrigger>
              <SelectContent>{GFM_SITES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button className="bg-brand hover:bg-brand-dark" onClick={addMapping} data-testid="add-mapping-btn"><Plus className="mr-1 h-4 w-4" /> Add</Button>
        </div>
        {mappings.length === 0 ? (
          <div className="px-4 py-8 text-center text-muted-foreground">No mappings yet. Departments that exactly match a site name are auto-mapped.</div>
        ) : (
          <table className="w-full text-sm" data-testid="mappings-table">
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id} className="border-t border-border" data-testid={`mapping-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium">{m.department}</td>
                  <td className="px-4 py-3 text-muted-foreground">→ {m.site}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => delMapping(m.id)} data-testid={`del-mapping-${m.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ApproveDialog open={!!approve} onOpenChange={(o) => !o && setApprove(null)} user={approve} onDone={load} />
    </AppShell>
  );
}
